<template>
  <section>
    <nav class="level">
      <div class="level-left">
        <div class="level-item">
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.activation_status') }}
              </span>
            </p>
            <b-select v-model="filterStatus" @input="onFilterStatus">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="true">{{ $t('attributes.shelve_on') }}</option>
              <option value="false">{{ $t('attributes.shelve_off') }}</option>
            </b-select>
          </b-field>
        </div>
      </div>

      <div class="level-right">
        <div class="level-item">
          <button class="button field is-primary" @click="onAdd">
            <b-icon icon="plus"></b-icon>
            <span>
              {{ $t('attributes.add') + $t('attributes.top_travel') }}
            </span>
          </button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :selected="selectedItem"
      :loading="isLoading"
      hoverable
      narrowed
      bordered
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      @select="onSelect"
    >
      <b-table-column
        field="sort"
        :label="$t('attributes.sort')"
        width="75"
        sortable
        numeric
        v-slot="props"
      >
        {{ props.row.sort }}
      </b-table-column>

      <b-table-column
        field="status"
        :label="$t('attributes.activation_status')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        <b-tag
          :type="props.row.status ? 'is-info' : 'is-danger'"
          size="is-medium"
        >
          {{ shelveStatus(props.row.status) }}
        </b-tag>
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.name')"
        sortable
        v-slot="props"
      >
        <p v-html="props.row.name"></p>
      </b-table-column>

      <b-table-column
        custom-key="actions"
        :width="80"
        label="功能"
        centered
        v-slot="props"
      >
        <div class="buttons">
          <b-button type="is-primary" @click.stop.prevent="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      :width="1200"
      has-modal-card
    >
      <top-travel-form :top-travel="editItem" @send="onSend" />
    </b-modal>
  </section>
</template>

<script>
import { TOP_TRAVEL_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import TopTravelForm from './TopTravelForm'

export default {
  name: 'TopTravelList',
  components: {
    TopTravelForm
  },

  props: {
    query: {
      type: Object,
      required: true
    },
    selected: {
      type: Object
    },
    loadingItem: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      loadingItems: false,
      items: [],
      selectedItem: null,

      filterStatus: this.query.status || '',
      sortField: this.query.sort || 'sort',
      sortOrder: this.query.order || 'asc',
      defaultSortOrder: 'asc',

      isFormModalActive: false,
      editItem: null,
      sending: false
    }
  },
  watch: {
    query(newQuery) {
      this.getQuery(newQuery)
      this.loadItems()
    },
    selected(item) {
      if (item) {
        this.items = this.items.map(x => {
          if (x.id === item.id) {
            return item
          } else {
            return x
          }
        })
      }
      this.selectedItem = item
    }
  },
  computed: {
    isLoading() {
      return this.loadingItems || this.loadingItem
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    async loadItems() {
      try {
        const api = `${TOP_TRAVEL_API}`
        const params = {
          status: this.filterStatus && this.filterStatus === 'true',
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc'
        }

        this.loadingItems = true
        const id = this.selectedItem && this.selectedItem.id

        const res = await this.axios.get(api, { params })
        const items = res.data.data
        this.items = items.sort((t1, t2) => t1.sort - t2.sort)

        if (id) {
          const item = this.items.find(x => x.id === id)
          this.onSelect(item)
        }
      } catch (e) {
        this.items = []
        this.onSelect(null)

        notifyService.fetch_error('top_travel', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.filterStatus = query.status || ''
      this.sortField = query.sort || 'sort'
      this.sortOrder = query.order || 'asc'
    },
    updateQuery() {
      const query = {
        status: this.filterStatus,
        sort: this.sortField,
        order: this.sortOrder
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilterStatus() {
      this.onSelect(null)
      this.updateQuery()
    },
    onSelect(item) {
      this.selectedItem = item
      this.$emit('update:selected', this.selectedItem)
    },
    onAdd() {
      this.editItem = {
        id: null,
        sort: 1,
        status: false,
        name: ''
      }
      this.isFormModalActive = true
    },
    onModify(item) {
      this.editItem = item
      this.isFormModalActive = true
    },
    onSend(item) {
      if (item.id == null) {
        this.createItem(item)
      } else {
        this.updateItem(item)
      }
    },
    async createItem(item) {
      try {
        const api = `${TOP_TRAVEL_API}`

        this.sending = true
        const res = await this.axios.post(api, item)
        const _item = res.data.data

        const items = [...this.items, _item]
        this.items = items.sort((t1, t2) => t1.sort - t2.sort)

        this.isFormModalActive = false
        notifyService.success(this.$t('attributes.add_succeed'))
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateItem(item) {
      try {
        const api = `${TOP_TRAVEL_API}/${item.id}`

        this.sending = true
        const res = await this.axios.put(api, item)
        const _item = res.data.data

        const idx = this.items.findIndex(s => s.id === _item.id)
        const items = [...this.items]
        items[idx] = _item
        this.items = items.sort((t1, t2) => t1.sort - t2.sort)

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
